/**
 * Ticket Master — Component RBAC Registry
 *
 * Source of truth for every component-level permission toggle inside the
 * Ticket Master dashboard. Modelled on `leadDetailsComponents.js`.
 *
 * Consumers:
 *   - src/config/routeTabConfig.js (TicketMasterDashboard.routes['/ticket-master'].components)
 *   - src/modules/user_management/store/permissionsStore.js (useComponentPermission)
 *   - src/modules/user_management/RoutePermissionsWizard.jsx (RBAC UI)
 *
 * Shape of each entry:
 *   label          — user-facing string shown in the RBAC toggle UI
 *   group          — bucket for visual grouping (see COMPONENT_GROUPS)
 *   defaultAllowed — value used when no DB row exists.
 *                    Sensitive toggles default to `false`; everything else `true`.
 *   description    — optional one-line hint for the RBAC UI tooltip
 *
 * Convention for keys: kebab-case.
 */

// Reuses the same group vocabulary as lead-details so the RBAC UI renders a
// consistent set of buckets. 'admin-tools' is the existing bucket for
// privileged actions, which is what a bulk export is.
import { COMPONENT_GROUPS } from './leadDetailsComponents';

export { COMPONENT_GROUPS };

export const TICKET_MASTER_COMPONENTS = {
  download: {
    label: 'Download CSV export',
    group: 'admin-tools',
    // Sensitive: exports bulk customer-linked ticket data (names, case ids,
    // comments) for a range of up to a month in a single file. Defaults OFF
    // and must be granted per role from the RBAC dashboard.
    defaultAllowed: false,
    description:
      'Shows the Download button on the Ticket Master table, which exports the full filtered result set as CSV (not just the visible page). Sensitive — default OFF.',
  },
};

/**
 * Sensitive keys — default OFF. Must be granted per role from the RBAC UI.
 * Kept as a separate export so a seed migration / UI can highlight these.
 */
export const SENSITIVE_COMPONENT_KEYS = Object.entries(TICKET_MASTER_COMPONENTS)
  .filter(([, meta]) => meta.defaultAllowed === false)
  .map(([key]) => key);
